'use client';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import { HTMLDataTrackerProps } from '@/app/types';
import MaxHeightCalcWrapper from '@/cms/MaxHeightCalcWrapper';
import {
  CodeBlockItem,
  CodeBlockItemProps,
  CodeBlockLanguage,
} from '@/decodo/components/atoms/CodeBlock/CodeBlockItem';
import CopyButton from '@/decodo/components/atoms/CodeBlock/CopyButton';
import { Tabs } from '@/decodo/components/atoms/Tabs';

export type CodeBlockProps = {
  items: {
    code: CodeBlockItemProps['code'];
    language: CodeBlockItemProps['language'];
  }[];
  showLineNumbers?: CodeBlockItemProps['showLineNumbers'];
  classNames?: {
    root?: string;
    languageTab?: string;
    codeBlockItem?: CodeBlockItemProps['classNames'];
  };
} & HTMLDataTrackerProps;

const maxContainerHeightPx = 500;
const verticalPaddingPx = 24;

export function CodeBlock({
  items,
  showLineNumbers,
  classNames,
  ...rest
}: CodeBlockProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [maxHeight, setMaxHeight] = useState(0);

  const handleMaxHeightCalculated = (h: number) => {
    const maxAllowed = maxContainerHeightPx - 2 * verticalPaddingPx;

    const height = h > maxAllowed ? maxAllowed : h;
    // compensate for padding of p-6
    setMaxHeight(height);
  };

  function handleTabChange(index: number) {
    setActiveTab(index);
  }

  const RenderCodeBlockItem = (
    item: CodeBlockItemProps,
    ContainerProps?: CodeBlockItemProps['ContainerProps']
  ) => (
    <CodeBlockItem
      key={item.language}
      code={item.code}
      language={item.language}
      showLineNumbers={showLineNumbers}
      ContainerProps={ContainerProps}
    />
  );

  const languages = items.map((item) => item.language);
  const shouldShowTabs = languages.length > 1;

  const SingleItemContent = () =>
    RenderCodeBlockItem(items[activeTab], {
      style: {
        height: shouldShowTabs ? maxHeight : undefined,
      },
    });

  return (
    <>
      <div
        className='bg-base-white dark relative w-full rounded px-2 pt-6 dark:border dark:border-solid'
        data-tracker-component='code-block'
        {...rest}
      >
        <div
          style={{ maxHeight: maxContainerHeightPx }}
          className={cn(
            'border-gray-dark-600 scrollbar flex w-full flex-col gap-6 overflow-auto rounded px-4 md:flex-row',
            // when changing this padding, make sure to update verticalPaddingPx to match
            classNames?.root
          )}
        >
          {shouldShowTabs ? (
            <Tabs<CodeBlockLanguage>
              label='Code language tabs'
              tabs={languages.map((lang) => ({
                content: <SingleItemContent />,
                label: lang,
                value: lang,
              }))}
              classNames={{
                tabContent: 'scrollbar',
                root: 'h-auto overflow-x-hidden',
              }}
              defaultValue={items[activeTab].language}
              value={items[activeTab].language}
              onValueChange={(value) => {
                const index = languages.findIndex((lang) => lang === value);

                handleTabChange(index);
              }}
            />
          ) : (
            <SingleItemContent />
          )}
        </div>
        <div className='dark relative flex shrink-0 justify-end px-6 pb-6 pt-2'>
          <CopyButton copyText={items[activeTab].code || ''} />
        </div>
      </div>
      <MaxHeightCalcWrapper onCalculated={handleMaxHeightCalculated}>
        {items.map((item) => RenderCodeBlockItem(item))}
      </MaxHeightCalcWrapper>
    </>
  );
}
